interface Service {
  num: string;
  title: string;
  blurb: string;
  bullets: string[];
  ideal: string;
  accent: string;
}

const SERVICES: Service[] = [
  {
    num: "01",
    title: "Applied AI engineering",
    blurb: "Agentic systems that ship — not demos. Eval harnesses, retrieval, tool-use, deployment, and the production-grade scaffolding to keep them reliable.",
    bullets: [
      "Forward-deployed: I work inside your repo",
      "Agent design, tools, and orchestration",
      "Evals, traces, and reliability budgets",
      "Claude / Anthropic API integrations",
    ],
    ideal: "Teams shipping their first real AI feature, or stuck moving one out of demo land.",
    accent: "var(--beam)",
  },
  {
    num: "02",
    title: "Full-stack product engineering",
    blurb: "Twelve years of classical product engineering — TypeScript, Python, Postgres, distributed systems. The unglamorous parts done right so the AI parts can actually run.",
    bullets: [
      "End-to-end product features",
      "API design & data modelling",
      "Cloud infra (AWS, Vercel, Cloudflare)",
      "Performance, observability, hardening",
    ],
    ideal: "Startups without a senior backend yet, or one engineer who needs a peer-level second.",
    accent: "var(--bio)",
  },
  {
    num: "03",
    title: "AI & agentic services — life, work, business",
    blurb: "Two halves of the same practice: turning your proof of concept into a production-ready product, and integrating AI and agentic workflows into every part of your life and business.",
    bullets: [
      "PoC → production: hardening, evals, deploy",
      "Agentic workflows for daily life & work",
      "Business process automation with agents",
      "Personal AI infrastructure, done properly",
    ],
    ideal: "Anyone with a promising prototype that needs to become real, or a life and business ready to run on agents.",
    accent: "var(--beam)",
  },
  {
    num: "04",
    title: "Network & smart-home systems",
    blurb: "Ubiquiti-grade IT for offices, studios, and homes — designed, deployed, and documented. Plus a consolidated smart-home build on Home Assistant that doesn't depend on someone else's cloud.",
    bullets: [
      "Ubiquiti UniFi: design, install, harden",
      "Consolidated smart-home build",
      "Home Assistant on Proxmox / Docker",
      "VLANs, VPN, segmentation, backups",
    ],
    ideal: "Solo founders, small studios, and homeowners who want serious infra without the enterprise bill.",
    accent: "var(--solar)",
  },
];

export function Services() {
  return (
    <section id="services" style={{ position: "relative", padding: "var(--section-y, 120px) 32px" }}>
      <div className="container">
        <div style={{ display: "flex", flexDirection: "column", gap: 24, maxWidth: 760, marginBottom: 64 }}>
          <span className="rule-eyebrow">Services</span>
          <h2
            style={{
              margin: 0,
              fontFamily: "var(--font-display)",
              fontWeight: 300,
              fontSize: "clamp(36px, 5vw, 60px)",
              lineHeight: 1.02,
              letterSpacing: "-0.02em",
              color: "var(--ink)",
            }}
          >
            Four practices, one operator. Pick one — or all of them.
          </h2>
          <p className="lead" style={{ maxWidth: 640 }}>
            Engagements are typically 3–12 weeks, embedded with your team. I bill weekly, scope monthly, and ship continuously.
          </p>
        </div>

        <div className="grid-3" style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 20 }}>
          {SERVICES.map((service) => (
            <article
              key={service.num}
              className="card"
              style={{
                background: "var(--surface)",
                border: "1px solid var(--hairline)",
                borderRadius: "var(--r-lg)",
                padding: 28,
                display: "flex",
                flexDirection: "column",
                gap: 18,
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute", top: 0, right: 0, width: 80, height: 80,
                  background: `radial-gradient(circle at top right, ${service.accent}, transparent 70%)`,
                  opacity: 0.18,
                  pointerEvents: "none",
                }}
              />

              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span className="num">— {service.num}</span>
                <span style={{ width: 8, height: 8, borderRadius: 999, background: service.accent, opacity: 0.85 }} />
              </div>

              <h3
                style={{
                  margin: 0,
                  fontFamily: "var(--font-display)",
                  fontWeight: 500,
                  fontSize: 26,
                  lineHeight: 1.15,
                  letterSpacing: "-0.01em",
                  color: "var(--ink)",
                }}
              >
                {service.title}
              </h3>

              <p style={{ margin: 0, fontFamily: "var(--font-body)", fontSize: 14, lineHeight: 1.6, color: "var(--ink-2)" }}>
                {service.blurb}
              </p>

              <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
                {service.bullets.map((bullet) => (
                  <li
                    key={bullet}
                    style={{
                      display: "flex",
                      gap: 10,
                      alignItems: "flex-start",
                      fontFamily: "var(--font-body)",
                      fontSize: 13,
                      color: "var(--ink-2)",
                      lineHeight: 1.5,
                    }}
                  >
                    <svg
                      width="14" height="14" viewBox="0 0 24 24" fill="none"
                      stroke={service.accent} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
                      style={{ flexShrink: 0, marginTop: 3 }}
                    >
                      <path d="M5 12h14" />
                    </svg>
                    {bullet}
                  </li>
                ))}
              </ul>

              <div
                style={{
                  marginTop: "auto",
                  paddingTop: 18,
                  borderTop: "1px solid var(--hairline)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                }}
              >
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 10,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    color: "var(--ink-4)",
                  }}
                >
                  Ideal for
                </span>
                <span style={{ fontFamily: "var(--font-body)", fontSize: 13, lineHeight: 1.5, color: "var(--ink-3)" }}>
                  {service.ideal}
                </span>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
